import React, { useEffect, useRef, useState } from 'react'
import { useProject } from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { buildNewColumn } from 'components/spreadsheet/helpers/functions'
import {
  createTableColumnViewAction,
  updateColumn,
  ICreateTableColumnView
} from 'components/spreadsheet/contexts/data/actions'
import { SpreadsheetReducerActions, ISpreadsheetData, IColumnTypes } from 'components/spreadsheet/types'
import { ITableViewColumn, IContextMenuState, SelectKindOptions } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import {
  ALL_TYPES,
  COLUMN_TYPE_NAMES,
  SELECT_COLUMN_TYPES,
  DEFAULT_KIND_OPTIONS
} from 'components/spreadsheet/constants/const'

// Column types offered in the picker, sorted by display name to match the edit-column settings menu.
const SORTED_COLUMN_TYPES: IColumnTypes[] = [...ALL_TYPES].sort((typeA, typeB) =>
  COLUMN_TYPE_NAMES[typeA].localeCompare(COLUMN_TYPE_NAMES[typeB])
)

// The two stages of the add-column flow: pick a type, or configure the column before creating it.
export type ColumnPickerStage = 'type' | 'config'

// A not-yet-created column being configured in stage 2. Same shape the create endpoint accepts.
export type ColumnDraft = ICreateTableColumnView

// Where a select column's options come from: a hand-entered list, or a source table column.
export type SelectOptionsMode = 'manual' | 'table'

// The segmented switcher's options, shared by the add-column configure stage and the Edit Column menu.
export const SELECT_OPTIONS_MODES: Array<{ value: SelectOptionsMode; label: string }> = [
  { value: 'manual', label: 'Manual list' },
  { value: 'table', label: 'From a table' }
]

// The source-table selection for a "from a table" select column, held until the column is created.
export interface SelectSource {
  tableId: string
  viewId: string
  columnId: string
  liveValues: boolean
  autopopulate: boolean
}

const EMPTY_SELECT_SOURCE: SelectSource = {
  tableId: '',
  viewId: '',
  columnId: '',
  liveValues: false,
  autopopulate: false
}

export interface ColumnTypePicker {
  menuState: IContextMenuState
  setMenuState: React.Dispatch<React.SetStateAction<IContextMenuState>>
  typeSearch: string
  setTypeSearch: (value: string) => void
  filteredTypes: IColumnTypes[]
  searchInputRef: React.RefObject<HTMLInputElement | null>
  nameInputRef: React.RefObject<HTMLInputElement | null>
  processing: boolean
  stage: ColumnPickerStage
  draft: ColumnDraft | null
  // The table the column belongs to — needed by the description editor for attachment uploads.
  tablePublicId: string
  // Manual select options being configured (select/multiselect only).
  optionsDraft: string[]
  // Whether typing an unknown value into a cell creates an option (select/multiselect only).
  expandOptionsViaCellEntry: boolean
  setExpandOptionsViaCellEntry: (value: boolean) => void
  addOption: (value: string) => void
  updateOption: (index: number, value: string) => void
  removeOption: (index: number) => void
  moveOption: (from: number, to: number) => void
  sortOptions: () => void
  // Whether a select column's options are a manual list or sourced from a table.
  optionsMode: SelectOptionsMode
  setOptionsMode: (mode: SelectOptionsMode) => void
  selectSource: SelectSource
  updateSelectSource: (patch: Partial<SelectSource>) => void
  // Open the picker at `anchor` for a column that would be inserted at `sortOrder`. No column is
  // created yet — creation happens on quick-add or on submitting the configure stage.
  openForNewColumn: (sortOrder: number, anchor: { top: string; left: string }) => void
  // Fast path: create a column of `kind` immediately with defaults, then close.
  quickAdd: (kind: IColumnTypes) => void
  // Opt-in path: build a draft of `kind` and switch to the configure stage.
  configure: (kind: IColumnTypes) => void
  // Merge a change from the configure form into the draft.
  updateDraft: (patch: Partial<ColumnDraft>) => void
  // Return from the configure stage to the type list without creating anything.
  back: () => void
  // Create the column described by the draft, then close.
  submit: () => void
  // Dismiss the picker (click away / Escape). Creates nothing.
  handleClose: () => void
}

// Shared behaviour for the two-stage "add a column" flow used by the header + button and by the column
// context menu's Insert (Before) action. Picking a type adds the column instantly with defaults;
// the per-row configure affordance opens a second stage that mirrors the Edit Column options and only
// creates the column on submit. Dismissing without submitting creates nothing.
export const useColumnTypePicker = (
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>
): ColumnTypePicker => {
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()
  const { setPendingRenameColumnId } = useDataContext()

  const [processing, setProcessing] = useState<boolean>(false)
  const [menuState, setMenuState] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [typeSearch, setTypeSearch] = useState<string>('')
  const [stage, setStage] = useState<ColumnPickerStage>('type')
  const [draft, setDraft] = useState<ColumnDraft | null>(null)
  const [optionsDraft, setOptionsDraft] = useState<string[]>([])
  const [expandOptionsViaCellEntry, setExpandOptionsViaCellEntry] = useState<boolean>(true)
  const [optionsMode, setOptionsMode] = useState<SelectOptionsMode>('manual')
  const [selectSource, setSelectSource] = useState<SelectSource>(EMPTY_SELECT_SOURCE)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  // Where the new column would be inserted, captured on open and read when the column is created.
  const pendingSortOrder = useRef<number>(0)

  const filteredTypes = SORTED_COLUMN_TYPES.filter((type) =>
    COLUMN_TYPE_NAMES[type].toLowerCase().includes(typeSearch.trim().toLowerCase())
  )

  // Imperative focus (the autoFocus JSX prop is disallowed by jsx-a11y/no-autofocus): the type search
  // when the list is shown, the name field when the configure stage opens.
  useEffect(() => {
    if (!menuState.open) return
    if (stage === 'type') searchInputRef.current?.focus()
    else nameInputRef.current?.focus()
  }, [menuState.open, stage])

  const handleFailure = (error: any): void => {
    setProcessing(false)
    displayErrorMessage(error)
  }

  // Return the flow to the type list, clearing any in-progress draft and its options.
  const clearToTypeStage = (): void => {
    setStage('type')
    setDraft(null)
    setOptionsDraft([])
    setOptionsMode('manual')
    setExpandOptionsViaCellEntry(true)
    setSelectSource(EMPTY_SELECT_SOURCE)
  }

  const reset = (): void => {
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
    setTypeSearch('')
    clearToTypeStage()
  }

  const finishCreate = (): void => {
    setProcessing(false)
    reset()
  }

  const buildDraft = (kind: IColumnTypes): ColumnDraft => ({
    ...buildNewColumn(
      spreadsheetData,
      pendingSortOrder.current,
      project.publicId,
      project.defaultHeaderBackgroundColor,
      project.defaultHeaderTextColor
    ),
    kind
  })

  const create = (column: ColumnDraft, onCreated: (created: ITableViewColumn) => void = finishCreate): void => {
    if (processing) return
    setProcessing(true)
    createTableColumnViewAction(
      spreadsheetData.viewDetails.publicId,
      column,
      setSpreadsheetData,
      onCreated,
      handleFailure
    )
  }

  const openForNewColumn = (sortOrder: number, anchor: { top: string; left: string }): void => {
    if (processing) return
    pendingSortOrder.current = sortOrder
    clearToTypeStage()
    setTypeSearch('')
    setMenuState({ ...INITIAL_CONTEXT_MENU_STATE, open: true, top: anchor.top, left: anchor.left })
  }

  // A quick-added column still has its generic default name, so open its header rename straight away.
  // The configure stage doesn't do this: the author has already named the column there.
  const quickAdd = (kind: IColumnTypes): void =>
    create(buildDraft(kind), (created) => {
      finishCreate()
      setPendingRenameColumnId(created.publicId)
    })

  const configure = (kind: IColumnTypes): void => {
    setDraft(buildDraft(kind))
    setOptionsDraft([])
    setOptionsMode('manual')
    setExpandOptionsViaCellEntry(true)
    setSelectSource(EMPTY_SELECT_SOURCE)
    setStage('config')
  }

  const updateSelectSource = (patch: Partial<SelectSource>): void => {
    setSelectSource((prev) => ({ ...prev, ...patch }))
  }

  const updateDraft = (patch: Partial<ColumnDraft>): void => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev))
  }

  const addOption = (value: string): void => {
    const trimmed = value.trim()
    if (!trimmed) return
    setOptionsDraft((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]))
  }

  const updateOption = (index: number, value: string): void => {
    setOptionsDraft((prev) => prev.map((option, position) => (position === index ? value : option)))
  }

  const removeOption = (index: number): void => {
    setOptionsDraft((prev) => prev.filter((_, position) => position !== index))
  }

  // Reorder an option (drag-and-drop): pull it out of `from` and reinsert it at `to`.
  const moveOption = (from: number, to: number): void => {
    setOptionsDraft((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  // One-shot sort of the manual options; toggles A→Z / Z→A on repeated use.
  const sortOptions = (): void => {
    setOptionsDraft((prev) => {
      const ascending = [...prev].sort((a, b) => a.localeCompare(b))
      const alreadyAscending = prev.every((value, index) => value === ascending[index])
      return alreadyAscending ? [...ascending].reverse() : ascending
    })
  }

  const back = (): void => clearToTypeStage()

  // A select column's options (manual list or source table) — null when there's nothing to persist.
  const buildSelectKindOptions = (kind: IColumnTypes): SelectKindOptions | null => {
    if (!SELECT_COLUMN_TYPES.includes(kind)) return null
    if (optionsMode === 'table' && selectSource.tableId && selectSource.viewId && selectSource.columnId) {
      return {
        ...DEFAULT_KIND_OPTIONS,
        tableOptions: {
          ...DEFAULT_KIND_OPTIONS.tableOptions,
          tableId: selectSource.tableId,
          viewId: selectSource.viewId,
          columnId: selectSource.columnId,
          liveValues: selectSource.liveValues
        },
        autopopulate: selectSource.autopopulate
      }
    }
    // Worth persisting for the cell-entry setting alone, since turning it off on a column with no
    // options yet is exactly how you set up a list only an admin can extend.
    if (optionsMode === 'manual' && (optionsDraft.length > 0 || !expandOptionsViaCellEntry)) {
      return { ...DEFAULT_KIND_OPTIONS, manualOptions: optionsDraft, expandOptionsViaCellEntry }
    }
    return null
  }

  const submit = (): void => {
    if (!draft) return
    const column = draft
    // The create endpoint's schema (CreateTableViewColumnSchema) doesn't declare kindOptions, and
    // BaseSchema rejects unknown fields (Marshmallow unknown=RAISE), so options can't ride along on the
    // POST — they're persisted in a follow-up kindOptions update (as Edit Column also does). Folding this
    // into one request would require extending the backend create schema to accept kindOptions.
    const kindOptions = buildSelectKindOptions(column.kind)

    create(column, (created) => {
      if (kindOptions) {
        // The kindOptions update endpoint rejects these server-managed fields — strip them before
        // sending (mirrors Edit Column's handleUpdateKindOptions).
        const payload = JSON.parse(JSON.stringify(kindOptions))
        delete payload.publicId
        delete payload.tableOptions.cachedOptions
        updateColumn(
          project.publicId,
          'kindOptions',
          spreadsheetData,
          setSpreadsheetData,
          created.publicId,
          payload,
          finishCreate,
          handleFailure
        )
      } else {
        finishCreate()
      }
    })
  }

  const handleClose = (): void => reset()

  return {
    menuState,
    setMenuState,
    typeSearch,
    setTypeSearch,
    filteredTypes,
    searchInputRef,
    nameInputRef,
    processing,
    stage,
    draft,
    tablePublicId: spreadsheetData.tableDetails.publicId,
    optionsDraft,
    expandOptionsViaCellEntry,
    setExpandOptionsViaCellEntry,
    addOption,
    updateOption,
    removeOption,
    moveOption,
    sortOptions,
    optionsMode,
    setOptionsMode,
    selectSource,
    updateSelectSource,
    openForNewColumn,
    quickAdd,
    configure,
    updateDraft,
    back,
    submit,
    handleClose
  }
}
